import { Formik } from 'formik'
import { Box, Button, HStack, Radio, Text, VStack, useToast } from 'native-base'
import {
  Dispatch,
  RefObject,
  ReactElement,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { isMobile } from 'react-device-detect'
import * as yup from 'yup'

import { useLazyQuery, useMutation } from '@apollo/client'
import { MapRef } from '@vis.gl/react-maplibre'

import client from '../../apolloClient'
import i18n from '../../i18n'
import HouseIconLightGreyBgSVG from '../../assets/HouseIconLightGreySVG'
import PinMarkerLightGreySVG from '../../assets/PinMarkerLightGreySVG'
import { AppContext } from '../../context/AppContext'
import { AuthContext } from '../../context/AuthContext'
import { IEditAddressProps, IToggleMapModalToastProps } from '../../context/OverlayContext'
import {
  Address,
  AGREE_TO_TERMS_AND_CONDITIONS_CWA,
  EDIT_ORDER_CWA,
  GET_ADDRESS_FROM_GEOCODE,
  NewAddressInput,
  Order,
  UPDATE_NORMALIZED_ADDRESS,
  UpdateNormalizedAddressMutation,
  UpdateNormalizedAddressMutationVariables,
} from '@consumer/graphql'
import { trackEvent } from '../../utils/analytics-v2'
import { logError } from '../../utils/monitorClient'
import { colors } from '../../utils/theme/configureTheme'
import ActionCard from '../ActionCard'
import { ModalContent } from '../Instructions/Common/ModalContent'
import { ToastAlert } from '../Instructions/Common/ToastAlert'
import { tcFormSchema } from '../Instructions/TermAndConditions/TermsAndConditions'

interface IAddressSelectionOption {
  badgeText?: string
  titleText?: string
  descriptionText?: string
  key: string
  id: string
  value: string
  addressValue?: Partial<Address> | 'neitherAreCorrect'
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: ReactElement<any> | null
  badgeColor: string
}

interface IAdjustGeopinModalProps {
  order: Order
  adjustGeopin: boolean
  mapRef: RefObject<MapRef> | undefined
  initialAddressCoords: {
    latitude: number
    longitude: number
  }
  addressMarkerCoords: {
    latitude: number
    longitude: number
  }
  currentAddress: Partial<Address>
  reroutableZipCodes: string[]
  toggleEditAddress:
    (({ clearAddressFields, locationUpdate, useLocationUpdate, toggleMap }: IEditAddressProps) => void) | undefined
  resetAddressMarker: () => void
  setAdjustGeopin: Dispatch<SetStateAction<boolean>>
  handleCloseMap: ({ showToastAfterClose, toastProps }: IToggleMapModalToastProps) => void
  handleSetOrder?: (order: Order) => void
  mapZoom: number
  handleToggleMapBanner: () => void
  useUpdateNormalizedAddress?: boolean
  acceptedTermsAndConditions?: boolean
}

/**
 * Calculate the latitude delta based on the OrderMap zoom level. Used to ensure the AddressMarker is above the ModalContent component
 * @param zoom the map zoom level.
 * @returns the latitude delta.
 */
const calculateLatitudeDelta = (zoom: number): number => {
  // Known delta values at specific zoom levels
  const zoom12Delta = 0.01483985346
  const zoom15Delta = 0.002409533943
  const zoom16Delta = 0.002235583703 // diff from zoom 15 to zoom 16 is 0.00017395024
  const zoom17Delta = 0.001018069672 // diff from zoom 16 to zoom 17 is 0.001217514031
  const zoom18Delta = 0.0004661157529 // diff from zoom 17 to zoom 18 is 0.0005519539191
  const zoom19Delta = 0.0002531911491 // diff from zoom 18 to zoom 19 is 0.0002129246038
  const zoom20Delta = 0.0001138591697 // diff from zoom 19 to zoom 20 is 0.0001393320204
  const zoom21Delta = 0.00006718610473 // diff from zoom 20 to zoom 21 is 0.00004667306497
  const zoom22Delta = 0.00003287956636 // diff from zoom 21 to zoom 22 is 0.00008097956336

  // Define zoom ranges and their corresponding deltas
  type ZoomRange = {
    min: number
    max: number
  } & ({ delta: number } | { startDelta: number; endDelta: number })

  const zoomRanges: ZoomRange[] = [
    { min: 0, max: 12, delta: zoom12Delta },
    { min: 12, max: 15, startDelta: zoom12Delta, endDelta: zoom15Delta },
    { min: 15, max: 16, startDelta: zoom15Delta, endDelta: zoom16Delta },
    { min: 16, max: 17, startDelta: zoom16Delta, endDelta: zoom17Delta },
    { min: 17, max: 18, startDelta: zoom17Delta, endDelta: zoom18Delta },
    { min: 18, max: 19, startDelta: zoom18Delta, endDelta: zoom19Delta },
    { min: 19, max: 20, startDelta: zoom19Delta, endDelta: zoom20Delta },
    { min: 20, max: 21, startDelta: zoom20Delta, endDelta: zoom21Delta },
    { min: 21, max: 22, startDelta: zoom21Delta, endDelta: zoom22Delta },
    { min: 22, max: Infinity, startDelta: zoom22Delta, endDelta: zoom22Delta },
  ]

  // Find the appropriate zoom range
  const range = zoomRanges.find(r => zoom > r.min && zoom <= r.max)

  if (!range) {
    return zoom17Delta // Fallback to zoom17Delta for any unexpected values - default OrderMap zoom is 17 when AddressMarker is valid
  }

  // If it's a single delta value (not a range)
  if ('delta' in range) {
    return range.delta
  }

  // Calculate interpolation for ranges
  const progress = (zoom - range.min) / (range.max - range.min)
  return range.startDelta + progress * (range.endDelta - range.startDelta)
}

export const AdjustGeopin: React.FC<IAdjustGeopinModalProps> = ({
  order,
  adjustGeopin = false,
  mapRef,
  toggleEditAddress,
  initialAddressCoords,
  addressMarkerCoords,
  currentAddress,
  reroutableZipCodes,
  resetAddressMarker,
  setAdjustGeopin,
  handleCloseMap,
  handleSetOrder,
  mapZoom,
  handleToggleMapBanner,
  useUpdateNormalizedAddress,
  acceptedTermsAndConditions,
}) => {
  const { setOrder } = useContext(AppContext)
  const { reloadUser } = useContext(AuthContext)
  const toast = useToast()
  const isMobileScreen = isMobile
  const [originalMarkerPosition, setOriginalMarkerPosition] = useState<{ latitude: number; longitude: number } | null>(
    null
  )

  const [agreedToTermsAndConditions, { loading: agreedToTermsAndConditionsLoading }] = useMutation(
    AGREE_TO_TERMS_AND_CONDITIONS_CWA,
    {
      client: client,
      onCompleted: reloadUser,
    }
  )
  const [getAddressFromGeocode, { loading: getAddressFromGeocodeLoading }] = useLazyQuery(GET_ADDRESS_FROM_GEOCODE, {
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    onError: error => {
      logError('getAddressFromGeocode error', { error })
    },
    client: client,
  })

  const [editOrder, { loading: editOrderLoading }] = useMutation(EDIT_ORDER_CWA, {
    errorPolicy: 'all',
    client: client,
    fetchPolicy: 'network-only',
    onCompleted: async data => {
      if (data?.editOrder?.address) {
        const updatedOrder = data?.editOrder as Order
        if (handleSetOrder) {
          handleSetOrder(updatedOrder)
        } else {
          setOrder(updatedOrder)
        }

        mapRef?.current?.panTo({
          lat: updatedOrder.address?.location?.lat ?? initialAddressCoords.latitude,
          lng: updatedOrder.address?.location?.lng ?? initialAddressCoords.longitude,
        })

        // Only reset and close after everything is updated
        handleReset()
        handleCloseMap({
          showToastAfterClose: true,
          toastProps: {
            title: 'Address updated!',
            description: 'Updated your address for this delivery',
            status: 'success',
          },
        })
        return
      }

      if (data === null) {
        handleReset()
        handleCloseMap({
          showToastAfterClose: true,
          toastProps: {
            title: 'Unable to Update Delivery Address',
            description:
              'Sorry, something went wrong updating your delivery address. Please try again or contact support.',
            status: 'error',
          },
        })
      }
    },
    onError: error => {
      logError('AdjustGeopin - editOrder error', { error })
      handleReset()
      handleCloseMap({
        showToastAfterClose: true,
        toastProps: {
          title: 'Unable to Update Delivery Address',
          description:
            'Sorry, something went wrong updating your delivery address. Please try again or contact support.',
          status: 'error',
        },
      })
    },
  })

  const [updateNormalizedAddress, { loading: updateNormalizedAddressLoading }] = useMutation<
    UpdateNormalizedAddressMutation,
    UpdateNormalizedAddressMutationVariables
  >(UPDATE_NORMALIZED_ADDRESS, {
    errorPolicy: 'all',
    client: client,
    fetchPolicy: 'network-only',
    onCompleted: async data => {
      if (data?.updateNormalizedAddress) {
        if (handleSetOrder) {
          handleSetOrder({
            ...order,
            address: {
              ...order.address,
              ...data?.updateNormalizedAddress,
            },
          })
        } else {
          setOrder({
            ...order,
            address: {
              ...order.address,
              ...data?.updateNormalizedAddress,
            },
          })
        }

        handleReset()
        handleCloseMap({
          showToastAfterClose: true,
          toastProps: { title: 'Your address has been updated!', description: '' },
        })
      } else {
        handleReset()
        handleCloseMap({
          showToastAfterClose: true,
          toastProps: {
            title: 'Unable to Update Delivery Address',
            description:
              'Sorry, something went wrong updating your delivery address. Please try again or contact support.',
            status: 'error',
          },
        })
      }
    },
    onError: error => {
      logError('AdjustGeopin - updateNormalizedAddress error', { error })
      handleReset()
      handleCloseMap({
        showToastAfterClose: true,
        toastProps: {
          title: 'Unable to Update Delivery Address',
          description:
            'Sorry, something went wrong updating your delivery address. Please try again or contact support.',
          status: 'error',
        },
      })
    },
  })

  const [updateAddress, setUpdateAddress] = useState<boolean>(false)
  const [updateAddressSelections, setUpdateAddressSelections] = useState<IAddressSelectionOption[]>([])
  const [invalidZipCode, setInvalidZipCode] = useState<boolean>(false)

  // Track previous coordinates to avoid unnecessary resets
  const prevAddressMarkerCoords = useRef<{ latitude: number; longitude: number } | null>(null)

  // Reset state when address marker coordinates change significantly
  useEffect(() => {
    let mounted = true
    if (prevAddressMarkerCoords.current) {
      const prev = prevAddressMarkerCoords.current
      const current = addressMarkerCoords

      // Calculate the difference between current and previous coordinates
      const latDiff = Math.abs(current.latitude - prev.latitude)
      const lngDiff = Math.abs(current.longitude - prev.longitude)

      // Only reset if there's a significant change (more than 0.0001 degrees)
      const significantChange = latDiff > 0.0001 || lngDiff > 0.0001

      if (significantChange && mounted) {
        setUpdateAddress(false)
        setUpdateAddressSelections([])
        setInvalidZipCode(false)
      }
    }

    // Update the ref with current coordinates
    prevAddressMarkerCoords.current = addressMarkerCoords
    return () => {
      mounted = false
    }
  }, [addressMarkerCoords])

  const handleReset = useCallback(() => {
    setAdjustGeopin(false)
    setUpdateAddress(false)
    setUpdateAddressSelections([])
    setInvalidZipCode(false)
    setOriginalMarkerPosition(null)
    prevAddressMarkerCoords.current = null
    resetAddressMarker()
    if (useUpdateNormalizedAddress) {
      handleToggleMapBanner()
    }
  }, [resetAddressMarker, setAdjustGeopin, handleToggleMapBanner, useUpdateNormalizedAddress])

  const handleCancelAdjustGeopin = useCallback(() => {
    handleReset()
    mapRef?.current?.panTo({
      lat: initialAddressCoords.latitude,
      lng: initialAddressCoords.longitude,
    })

    if (useUpdateNormalizedAddress) {
      handleCloseMap({ showToastAfterClose: false })
    }
  }, [mapRef, initialAddressCoords, handleReset, useUpdateNormalizedAddress, handleCloseMap])

  const handleSaveAdjustGeopin = useCallback(async () => {
    try {
      // Store original position on first save
      if (!originalMarkerPosition) {
        setOriginalMarkerPosition({
          latitude: addressMarkerCoords.latitude,
          longitude: addressMarkerCoords.longitude,
        })
      }

      const result = await getAddressFromGeocode({
        variables: {
          lat: addressMarkerCoords.latitude,
          lng: addressMarkerCoords.longitude,
          consumerId: '',
        },
      })
      const reverseGeocodedAddressResult = result?.data?.getAddressFromGeocode

      if (!reroutableZipCodes?.includes(reverseGeocodedAddressResult?.zipCode)) {
        setInvalidZipCode(true)
        return
      }

      setUpdateAddressSelections([
        {
          badgeText: 'Keep Current Address',
          titleText: `${
            currentAddress?.apartment && currentAddress?.apartment?.length > 0
              ? `${currentAddress.street} ${currentAddress.apartment}`
              : currentAddress.street
          }`,
          descriptionText: `${currentAddress.city}, ${currentAddress.state} ${currentAddress.zipCode}`,
          key: 'keepCurrentAddress',
          id: 'keepCurrentAddressId',
          value: '1',
          addressValue: {
            ...currentAddress,
            location: { lat: addressMarkerCoords.latitude, lng: addressMarkerCoords.longitude },
          },
          icon: <HouseIconLightGreyBgSVG />,
          badgeColor: 'info',
        },
        {
          badgeText: 'Use New Address',
          titleText: `${
            reverseGeocodedAddressResult?.apartment && reverseGeocodedAddressResult?.apartment?.length > 0
              ? `${reverseGeocodedAddressResult.street} ${reverseGeocodedAddressResult.apartment}`
              : reverseGeocodedAddressResult.street
          }`,
          descriptionText: `${reverseGeocodedAddressResult.city}, ${reverseGeocodedAddressResult.state} ${reverseGeocodedAddressResult.zipCode}`,
          key: 'useNewAddress',
          id: 'useNewAddressId',
          value: '2',
          addressValue: reverseGeocodedAddressResult,
          icon: <PinMarkerLightGreySVG />,
          badgeColor: 'lightWarn',
        },
        {
          badgeText: 'Neither Are Correct',
          titleText: 'Add a new address',
          descriptionText: undefined,
          key: 'neitherAreCorrect',
          id: 'neitherAreCorrectId',
          value: '3',
          addressValue: 'neitherAreCorrect',
          icon: <HouseIconLightGreyBgSVG />,
          badgeColor: 'gray',
        },
      ])
      const latitudeDelta = calculateLatitudeDelta(mapZoom)
      if (mapRef?.current) {
        // Calculate offset from original position
        const mapCoordsWithLatitudeDelta = {
          lat: originalMarkerPosition
            ? originalMarkerPosition.latitude - latitudeDelta
            : addressMarkerCoords.latitude - latitudeDelta,
          lng: addressMarkerCoords.longitude,
        }
        mapRef.current.panTo(mapCoordsWithLatitudeDelta)
      }

      setUpdateAddress(true)
    } catch {
      setUpdateAddress(false)
      toast.show({
        placement: 'top',
        render: ({ id }) => (
          <ToastAlert
            id={id}
            title={'Unable to Update Delivery Address'}
            description={'Sorry, something went wrong updating your delivery address. Please try again.'}
            status={'error'}
            variant={'subtle'}
            isClosable={false}
            closeToast={toast.close}
          />
        ),
      })
    }
  }, [
    addressMarkerCoords,
    currentAddress,
    mapRef,
    reroutableZipCodes,
    getAddressFromGeocode,
    toast,
    mapZoom,
    originalMarkerPosition,
  ])

  const handleSaveUpdatedAddress = useCallback(
    async (values: { updateAddressChoice: string }) => {
      const updateAddressChoice = updateAddressSelections.find(
        selection => selection.value === values.updateAddressChoice
      )
      if (!updateAddressChoice?.addressValue) {
        toast.show({
          placement: 'top',
          render: ({ id }) => (
            <ToastAlert
              id={id}
              title={'Please Select an Option'}
              description={'Select an address and try again.'}
              status={'error'}
              variant={'subtle'}
              isClosable={false}
              closeToast={toast.close}
            />
          ),
        })
        return
      } else if (updateAddressChoice?.addressValue === 'neitherAreCorrect') {
        // reset state, close map modal, open edit address modal
        const geocodeUpdate = { latitude: addressMarkerCoords.latitude, longitude: addressMarkerCoords.longitude }
        handleCloseMap({ showToastAfterClose: false })
        if (toggleEditAddress) {
          toggleEditAddress({
            clearAddressFields: true,
            locationUpdate: { latitude: geocodeUpdate.latitude, longitude: geocodeUpdate.longitude },
            useLocationUpdate: true,
            toggleMap: true,
          })
        }
        setUpdateAddress(false)
        setUpdateAddressSelections([])
      } else {
        const newAddressInput: NewAddressInput = {
          city: updateAddressChoice?.addressValue?.city ?? '',
          state: updateAddressChoice?.addressValue?.state ?? '',
          zipCode: updateAddressChoice?.addressValue?.zipCode ?? '',
          name: order?.address?.name ?? '',
          street: updateAddressChoice?.addressValue?.street ?? '',
          apartment: updateAddressChoice?.addressValue?.apartment ?? '',
          location: {
            lat: updateAddressChoice?.addressValue?.location?.lat,
            lng: updateAddressChoice?.addressValue?.location?.lng,
            // TO DO: add precision level manual
          },
          userServedGeocodeCorrectionFlag: true,
          // default to true to avoid user being shown PermanentAddressUpdate modal
          consumerSpecifiedAddressNormalization: order?.address?.consumerSpecifiedAddressNormalization ?? true,
        }

        await editOrder({
          errorPolicy: 'none',
          notifyOnNetworkStatusChange: true,
          fetchPolicy: 'network-only',
          variables: {
            orderId: order?.orderId,
            addressInput: newAddressInput,
          },
        })
      }
    },
    [toggleEditAddress, updateAddressSelections, order, editOrder, handleCloseMap, addressMarkerCoords, toast]
  )

  const handleUpdateNormalizedAddress = useCallback(async () => {
    try {
      if (!order?.address?.normalizedAddressId || !order?.originalAddress) {
        toast.show({
          placement: 'top',
          render: ({ id }) => (
            <ToastAlert
              id={id}
              title={'Unable to Update Delivery Address'}
              description={'Sorry, something went wrong updating your delivery address. Please try again.'}
              status={'error'}
              variant={'subtle'}
              isClosable={false}
              closeToast={toast.close}
            />
          ),
        })
        return
      }

      const originalAddressInput: NewAddressInput = {
        city: order?.originalAddress?.city ?? '',
        state: order?.originalAddress?.state ?? '',
        zipCode: order?.originalAddress?.zipCode ?? '',
        name: order?.address?.name ?? '',
        street: order?.originalAddress?.street ?? '',
        apartment: order?.originalAddress?.apartment ?? '',
        location: {
          lat: addressMarkerCoords.latitude,
          lng: addressMarkerCoords.longitude,
        },
        userServedGeocodeCorrectionFlag: true,
        // default to true to avoid user being shown PermanentAddressUpdate modal
        consumerSpecifiedAddressNormalization: order?.address?.consumerSpecifiedAddressNormalization ?? true,
      }

      await updateNormalizedAddress({
        variables: {
          normalizedAddressId: order?.address?.normalizedAddressId as string,
          normalizedAddressInput: originalAddressInput,
          targetOrderId: order?.orderId as string,
        },
      })
    } catch (error) {
      logError('AdjustGeopin - handleUpdateNormalizedAddress error', { error })
      handleReset()
      handleCloseMap({
        showToastAfterClose: true,
        toastProps: {
          title: 'Unable to Update Delivery Address',
          description: 'Sorry, something went wrong updating your delivery address. Please try again.',
          status: 'error',
        },
      })
    }
  }, [order, addressMarkerCoords, toast, updateNormalizedAddress, handleReset, handleCloseMap])

  const handleCancelUpdatedAddress = useCallback(() => {
    setUpdateAddress(false)
    mapRef?.current?.panTo({
      lat: initialAddressCoords.latitude,
      lng: initialAddressCoords.longitude,
    })
  }, [initialAddressCoords, mapRef])

  const handleContactSupport = useCallback(() => {
    // TO DO: remove guard condition
    if (process.env.NODE_ENV === 'production') {
      trackEvent('contact_support', { method: 'sms' })
    }
    window.location.href = `sms:68953`
  }, [])

  const InvalidZipcode = useCallback(() => {
    return (
      <VStack
        style={{
          position: 'absolute',
          bottom: isMobileScreen ? 80 : 30,
          width: isMobileScreen ? '90%' : '33%',
          right: isMobileScreen ? '6.75%' : '35.5%',
          zIndex: 3000,
          backgroundColor: colors.brand.warmWhite,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: 8,
          padding: 18,
        }}
      >
        <VStack display="flex" alignItems={'center'}>
          <Text fontSize={20} fontWeight={500} mb={2}>
            Not In Our Service Area
          </Text>
          <Text fontSize={14} fontWeight={400} mb={6} textAlign={'center'}>
            We're sorry, but we don't currently deliver to this area. Please contact Veho support or update to a
            different delivery location
          </Text>
        </VStack>
        <HStack style={{ width: 320, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            style={{ backgroundColor: colors.brand.black, width: 145, paddingVertical: 8 }}
            onPress={handleCancelAdjustGeopin}
          >
            <Text color={colors.brand.white}>{i18n.t('common:CANCEL')}</Text>
          </Button>
          <Button
            style={{ backgroundColor: colors.brand.vermillion, width: 145, paddingVertical: 8 }}
            onPress={handleContactSupport}
          >
            <Text color={colors.brand.white}>{i18n.t('common:CONTACT_SUPPORT')}</Text>
          </Button>
        </HStack>
      </VStack>
    )
  }, [handleCancelAdjustGeopin, isMobileScreen, handleContactSupport])

  const AdjustGeopinButtons = useCallback(() => {
    return (
      <Box
        style={{
          position: 'absolute',
          width: isMobileScreen ? '80%' : '33%',
          bottom: isMobileScreen ? 80 : 30,
          right: isMobileScreen ? '9.5%' : '33%',
          zIndex: 3000,
          backgroundColor: 'transparent',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <HStack style={{ width: 300, justifyContent: 'space-between' }}>
          <Button
            style={{ backgroundColor: colors.brand.black, width: 120, paddingVertical: 8, opacity: 1.0 }}
            onPress={handleCancelAdjustGeopin}
          >
            <Text color={colors.brand.white}>{i18n.t('common:CANCEL')}</Text>
          </Button>
          <Button
            style={{ backgroundColor: colors.brand.vermillion, width: 120, paddingVertical: 8, opacity: 1.0 }}
            onPress={useUpdateNormalizedAddress ? handleUpdateNormalizedAddress : handleSaveAdjustGeopin}
            isLoading={getAddressFromGeocodeLoading ?? updateNormalizedAddressLoading}
          >
            <Text color={colors.brand.white}>Save Pin</Text>
          </Button>
        </HStack>
      </Box>
    )
  }, [
    handleCancelAdjustGeopin,
    handleSaveAdjustGeopin,
    getAddressFromGeocodeLoading,
    isMobileScreen,
    updateNormalizedAddressLoading,
    useUpdateNormalizedAddress,
    handleUpdateNormalizedAddress,
  ])

  if (!adjustGeopin) {
    return null
  } else if (updateAddress) {
    let initialValues, validationSchema, onSubmit
    if (!acceptedTermsAndConditions) {
      initialValues = { updateAddressChoice: '', acceptedTerms: false }
      validationSchema = yup
        .object()
        .shape({
          updateAddressChoice: yup.string().required('Please select an option'),
        })
        .concat(tcFormSchema)
      onSubmit = async (values: { updateAddressChoice: string }) => {
        await agreedToTermsAndConditions()
        await handleSaveUpdatedAddress(values)
      }
    } else {
      initialValues = { updateAddressChoice: '' }
      validationSchema = yup.object().shape({
        updateAddressChoice: yup.string().required('Please select an option'),
      })
      onSubmit = handleSaveUpdatedAddress
    }
    return (
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        isInitialValid={false}
        onSubmit={onSubmit}
      >
        {({ values, setFieldValue, isValid, submitForm, isSubmitting }) => (
          <ModalContent
            headerMain={'Update Address'}
            headerSecondary={
              "You moved the pin to a location that's different than the current address. Would you like to update the address?"
            }
            submitText={'Confirm'}
            cancelText={i18n.t('common:CANCEL')}
            onSubmit={submitForm}
            closeModal={handleCancelUpdatedAddress}
            disabled={!isValid}
            loading={
              isSubmitting ?? editOrderLoading ?? updateNormalizedAddressLoading ?? agreedToTermsAndConditionsLoading
            }
            useAddInstructionsLaterButton={false}
            modalStyles={{
              position: 'absolute',
              bottom: isMobileScreen ? 20 : 10,
              width: isMobileScreen ? '85%' : '35%',
              right: isMobileScreen ? '7.5%' : '33%',
              zIndex: 3000,
            }}
            includeTermsAndConditions={!acceptedTermsAndConditions}
            termsAndConditionsAction={'updating my location'}
            submitButtonStyles={{ minWidth: isMobileScreen ? 300 : 345 }}
            cancelButtonStyles={{ minWidth: isMobileScreen ? 300 : 345, backgroundColor: colors.brand.black }}
            headerContainerStyles={{ maxWidth: 345 }}
            headerMainStyles={{ fontWeight: 500, fontSize: 20, lineHeight: 20 }}
            headerSecondaryStyles={{ fontWeight: 400, fontSize: 12, lineHeight: 15 }}
          >
            <VStack>
              <Radio.Group
                name="updateAddressAndGeolocation"
                accessibilityLabel="selection for updating address geolocation"
                value={values.updateAddressChoice}
                onChange={value => setFieldValue('updateAddressChoice', value)}
                isDisabled={isSubmitting}
              >
                {updateAddressSelections.map(selection => {
                  return (
                    <ActionCard
                      CardIcon={selection?.icon}
                      key={selection?.key}
                      titleText={selection?.titleText}
                      titleTextStyles={{ fontSize: 16, fontWeight: 400, lineHeight: 17 }}
                      badgeText={selection?.badgeText}
                      descriptionText={selection?.descriptionText}
                      descriptionTextStyles={{ fontSize: 16, fontWeight: 400, lineHeight: 17 }}
                      cardAction="radioSelect"
                      radioValue={selection.value}
                      cardStyleProps={{
                        width: isMobileScreen ? '300px' : '345px',
                        marginBottom: 2,
                        borderRadius: 8,
                        paddingHorizontal: '16px',
                        paddingVertical: '16px',
                        borderWidth: 1,
                      }}
                      isSelected={values.updateAddressChoice === selection.value}
                      selectedBorderColor={colors.brand.selectedBlue}
                      selectedBackgroundColor={colors.brand.white}
                      badgeColor={selection?.badgeColor}
                      onPress={() => setFieldValue('updateAddressChoice', selection.value)}
                      isDisabled={isSubmitting}
                    />
                  )
                })}
              </Radio.Group>
            </VStack>
          </ModalContent>
        )}
      </Formik>
    )
  } else if (invalidZipCode) {
    return <InvalidZipcode />
  } else {
    return <AdjustGeopinButtons />
  }
}
